import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser'
import { LOGIN_REQUEST } from '../hooks/auth'
import { acquireInteraction, releaseInteraction } from './interaction'

/**
 * Renew an expired session without ejecting the user — see
 * docs/specs/7665-logout-interaction-in-progress/spec.md (Phase 2).
 *
 * `acquireTokenSilent` throws `InteractionRequiredAuthError` once the SPA refresh token hits the
 * 24-hour lifetime Entra applies to it. The user's *Entra session cookie* is usually still valid at
 * that point, so a hidden-iframe SSO round-trip renews them invisibly. Signing them out instead —
 * which is what CurvoX did — discards a good session and drops them at the front door.
 *
 * Single-flight: a batch of operations resuming together all wait on one renewal, and the
 * interactive fallback runs at most once for that batch.
 */
let renewal: Promise<string | null> | null = null

export function renewSession(instance: IPublicClientApplication, account: AccountInfo): Promise<string | null> {
  if (!renewal) {
    // The shared promise only merges *concurrent* callers, and is dropped as soon as it settles.
    // What bounds the retry is the interaction lock: while the sign-in fallback is redirecting it
    // holds the lock for good, so a later attempt declines without touching ssoSilent. If that
    // redirect fails instead, the lock comes back and the next request may renew again. Caching the
    // settled null here would strand every later request without a token (PR 7037 review).
    renewal = attemptRenewal(instance, account).finally(() => {
      renewal = null
    })
  }
  return renewal
}

async function attemptRenewal(instance: IPublicClientApplication, account: AccountInfo): Promise<string | null> {
  // Another interaction holds the lock — normally a redirect taking the page away, so there is
  // nothing left to renew for.
  if (!acquireInteraction('ssoSilent')) {
    return null
  }

  let token: string | null = null
  try {
    // An empty token is a failure, not a success: passing it on would send the request without an
    // Authorization header and skip the fallback (PR 7037 review).
    token = (await instance.ssoSilent({ ...LOGIN_REQUEST, account })).idToken || null
  } catch (error) {
    console.error('ssoSilent failed; falling back to an interactive sign-in:', error)
  } finally {
    // ssoSilent hands control back to the page either way, so the lock must come back — and with
    // it any logout the user requested while the renewal was running.
    releaseInteraction()
  }

  if (token === null) {
    // No-op if the released lock was just taken by a queued logout: an explicit Log Out outranks
    // getting the user signed back in.
    loginRedirectOnce(instance)
  }

  return token
}

/**
 * The interactive last resort: sign in again, landing back on the current page. Never a sign-out —
 * the user is mid-task, and `state` is what carries them back (PostLoginNavigator in App.tsx).
 */
export function loginRedirectOnce(instance: IPublicClientApplication): void {
  if (!acquireInteraction('loginRedirect')) {
    return
  }

  try {
    // Like logoutRedirect, the lock is held until the page unloads.
    Promise.resolve(
      instance.loginRedirect({ ...LOGIN_REQUEST, state: window.location.pathname + window.location.search }),
    ).catch(error => {
      releaseInteraction()
      console.error('loginRedirect failed:', error)
    })
  } catch (error) {
    releaseInteraction()
    console.error('loginRedirect threw:', error)
  }
}
